import { ObjectRule } from '@/classes/rules/ObjectRule';
import { MinMaxRule } from '@/classes/rules/MinMaxRule';
import { ScenarioPVExt } from '@/classes/scenario/pv/ScenarioPVExt';
import { OptionsRule } from '@/classes/rules/OptionsRule';
import { EnumRule } from '@/classes/rules/EnumRule';
import { PVFileExt } from '@/classes/scenario/pv/PVFileExt';
import { PVFileDurationRef, PVFileFormatRef, PVFileStepRef } from '@/dto/scenario/pv/PVReference';
import { DateRule } from '@/classes/rules/DateRule';
import { DefaultValue } from '@/classes/rules/DefaultValue';
import { ArrayRule } from '@/classes/rules/ArrayRule';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { BooleanRule } from '@/classes/rules/BooleanRule';
import { FieldUsage } from '@/classes/rules/FieldUsage';
import { InjectionModelRef } from '@/dto/scenario/ScenarioReference';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';
import { InstallationTypeRef, SurfaceTypeRef } from '@/dto/scenario/pv/SurfaceDefinition';
import { SurfaceDefinitionExt } from '@/classes/scenario/pv/SurfaceDefinitionExt';
import { normalizeFloat } from '../mathHelper';
import { NumberRule } from '@/classes/rules/NumberRule';

interface Dependencies {
  pvModelOptions: string[]
}

// TODO can be simplified because default, min and max are overridden by hysize spec file
export const buildPvRules = ({ pvModelOptions }: Dependencies) => new ObjectRule<ScenarioPVExt>('pv', [
  new BooleanRule('hasUploadedPV')
    .withIsAvailable(scenario => (scenario.project?.typeRef !== ProjectTypeRef.utility_scale_pv_plant_project && scenario.project?.typeRef !== ProjectTypeRef.wind_onshore_project) &&
      scenario.hasInjectionType(InjectionModelRef.total_injection)),

  new BooleanRule('hasSurfaceDefinition')
    .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project) &&
    scenario.hasInjectionType(InjectionModelRef.total_injection)),

  new MinMaxRule('forcedAnnualYieldResults')
    .withIsAvailable(scenario => !(scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project && scenario?.pv?.hasSurfaceDefinition))
    .withDefaultValue(scenario => {
      const { hasUploadedPV, file } = scenario?.pv ?? {};
      const { simulatedPVCapacity, values } = file ?? {};
      if (!hasUploadedPV || !simulatedPVCapacity || !values) {
        return null;
      }
      const annualYieldResults = values.reduce((sum, value) => sum + value) / simulatedPVCapacity;
      const rounded = normalizeFloat(annualYieldResults, 5);
      return new DefaultValue(rounded).withLock();
    })
    .withFieldUsage(new FieldUsage({ pvCurve: true })),

  new NumberRule('computedAnnualYieldResults', 'kWh/kWp')
    .withIsAvailable((scenario) => isRenExplorerProject(scenario) &&
      (scenario.pv?.hasSurfaceDefinition ?? false) &&
      scenario.pv?.annualYieldResults !== null),

  new MinMaxRule('pvCapacity')
    .withFactor(0.001)
    .withIsAvailable((scenario) => scenario.hasInjectionType(InjectionModelRef.total_injection) &&
      (!(scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project) || scenario.pv?.hasSurfaceDefinition === false)),

  new NumberRule('energy')
    .withFactor(0.001)
    .withIsAvailable((scenario) => scenario.hasInjectionType(InjectionModelRef.total_injection) &&
      scenario.pv?.energy !== null),

  new OptionsRule<string>('moduleRef', pvModelOptions), // Dynamically fed by ruleService

  new MinMaxRule('invertersLifetime', 0, 30, 'years')
    .withDefaultValue(() => new DefaultValue(15)),

  new MinMaxRule('maintenanceEfficiency', 0.9, 1, '%').withFactor(100)
    .withDefaultValue(() => new DefaultValue(0.98))
    .withIsAvailable(bypassableFieldInTotalInjection)
    .withFieldUsage(new FieldUsage({ pvCurve: true })),

  new MinMaxRule('panelCapacity', 0, 1000),

  new MinMaxRule('panelArea', 0, 10),

  new MinMaxRule('panelDegradation', 0, 1, '%').withFactor(100),

  new ObjectRule<PVFileExt>('file', [
    new MinMaxRule('simulatedPVCapacity', 0, 1000000, 'kwp')
      .withFieldUsage(new FieldUsage({ pvCurve: true })),

    new EnumRule('formatRef', PVFileFormatRef),

    new EnumRule('durationRef', PVFileDurationRef),

    new EnumRule('stepRef', PVFileStepRef),

    new DateRule('startDate'),

    new ArrayRule('values', scenario => (!!scenario.pv) || false,
      new MinMaxRule('sample', 0),
      val => (val.length === 8760), undefined, true)
      .withFieldUsage(new FieldUsage({ pvCurve: true }))
  ])
    .withIsAvailable(bypassableFieldInTotalInjection),

  buildSurfaceDefinitionRules()
])
  .withIsAvailable(scenario => !isRenFranceProject(scenario))
  .withDefaultValue(() => new DefaultValue(new ScenarioPVExt()));

function bypassableFieldInTotalInjection(scenario: ScenarioExt): boolean {
  if (scenario.hasInjectionType(InjectionModelRef.total_injection)) {
    if ((scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project)) {
      return false;
    }
    return scenario.pv?.hasUploadedPV ?? false;
  }
  return true;
}

function isRenExplorerProject(scenario: ScenarioExt): boolean {
  return (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project || scenario.project?.typeRef === ProjectTypeRef.wind_onshore_project) &&
    scenario.hasInjectionType(InjectionModelRef.total_injection);
}

function isRenFranceProject(scenario: ScenarioExt): boolean {
  return (scenario.project?.typeRef === ProjectTypeRef.ren_fr_pv_project || scenario.project?.typeRef === ProjectTypeRef.ren_fr_wind_project) &&
    scenario.hasInjectionType(InjectionModelRef.total_injection);
}

export function buildSurfaceDefinitionRules() {
  return new ObjectRule<SurfaceDefinitionExt>('surfaceDefinition', [
    new EnumRule('surfaceTypeRef', SurfaceTypeRef),

    new MinMaxRule('completeAvailableArea'),

    new MinMaxRule('areaOfExclusion')
      .withMaximum((scenario) => {
        if (scenario.pv?.surfaceDefinition?.completeAvailableArea) {
          return scenario.pv.surfaceDefinition.completeAvailableArea - 1;
        }
      }),

    new NumberRule('lostArea'),

    new NumberRule('netUsableArea'),

    new MinMaxRule('moduleEfficiency'),

    new MinMaxRule('panelCapacity', 0, 1000)
      .withIsAvailable(scenario => !(scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project)),

    new MinMaxRule('panelArea', 0, 10)
      .withIsAvailable(scenario => !(scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project)),

    new MinMaxRule('panelDegradation', 0, 1, '%').withFactor(100)
      .withIsAvailable(scenario => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project)),

    new MinMaxRule('pvPlantPower')
      .withFactor(0.001)
      .withPrecision(1)
      .withMinimum(context => context.pv?.surfaceDefinition?.computePvPlantPower(0.25) ?? undefined)
      .withMaximum(context => context.pv?.surfaceDefinition?.computePvPlantPower(0.93) ?? undefined),

    new MinMaxRule('groundCoverageRatio'),

    new MinMaxRule('relativeColumnSpacing'),

    new EnumRule('installationTypeRef', InstallationTypeRef)
      .withFilterOptions(([, value], context) => {
        const recommendation = context?.pv?.surfaceDefinition?.installationTypeRecommendation(value as InstallationTypeRef); // TODO find a way to remove the cast
        return recommendation === 'valid' || recommendation === 'disadvised';
      }),

    new MinMaxRule('yieldFromProspect'),

    new BooleanRule('bifacialModule')
      .withDefaultValue((scenario) => {
        if (scenario?.pv?.surfaceDefinition?.groundCoverageRatio === null) {
          return null;
        }
        return new DefaultValue(scenario!.pv!.surfaceDefinition!.computedBifacialModule).withLock();
      }),

    new MinMaxRule('albedoFromProspect')
      .withIsAvailable((scenario) => !!scenario?.pv?.surfaceDefinition?.bifacialModule),

    new MinMaxRule('bifacialGain')
      .withIsAvailable((scenario) => !!scenario?.pv?.surfaceDefinition?.bifacialModule),

    new MinMaxRule('systemUnavailability'),

    new MinMaxRule('gridUnavailability'),

    new MinMaxRule('otherLosses')
  ])
    .withIsAvailable((scenario) => (scenario.project?.typeRef === ProjectTypeRef.utility_scale_pv_plant_project && scenario.pv?.hasSurfaceDefinition) ?? false)
    .withDefaultValue(() => new DefaultValue(new SurfaceDefinitionExt()));
}
